import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Flag,
  ImageIcon,
  Layers,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  X,
} from "lucide-react";
import { Dialog as RadixDialog } from "radix-ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useSummaries, useSummary, useSummaryAction, useSummarySources } from "@/api/hooks";
import type { RawMessage, Summary, SummaryDetail } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { Chip, StatusChip } from "@/components/StatusChip";
import { Banner } from "@/components/ui/Banner";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Empty } from "@/components/ui/Empty";
import { Segmented } from "@/components/ui/Segmented";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { type MessageKey, useI18n } from "@/i18n";
import { cx } from "@/lib/cx";

type Filter = "draft" | "flagged" | "approved" | "published" | "rejected" | "all";

const FILTERS: Record<Filter, { status?: string; flagged?: boolean }> = {
  draft: { status: "draft" },
  flagged: { flagged: true },
  approved: { status: "approved" },
  published: { status: "published" },
  rejected: { status: "rejected" },
  all: {},
};

function useDebounced<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function WindowLabel({ summary }: { summary: Summary }) {
  const { t, fmt } = useI18n();
  if (summary.kind === "digest")
    return <>{t("review.digestOf", { date: fmt.date(summary.run.window_end) })}</>;
  return (
    <span className="num">
      {fmt.date(summary.run.window_start)}, {fmt.time(summary.run.window_start)}–
      {fmt.time(summary.run.window_end)}
    </span>
  );
}

function QueueItem({ summary, active }: { summary: Summary; active: boolean }) {
  const { t, fmt } = useI18n();
  return (
    <li>
      <Link
        to={`/review/${summary.id}`}
        aria-current={active ? "true" : undefined}
        className={cx(
          "flex flex-col gap-1.5 border-b border-line px-4 py-3.5 transition-colors",
          active
            ? "bg-surface shadow-[inset_3px_0_0_var(--c-accent)] rtl:shadow-[inset_-3px_0_0_var(--c-accent)]"
            : "hover:bg-[color-mix(in_oklab,var(--c-surface)_60%,transparent)]",
        )}
      >
        <div className="flex items-center justify-between gap-2 text-xs text-muted">
          <WindowLabel summary={summary} />
          <span className="flex items-center gap-1.5">
            {summary.sensitivity_flag && (
              <Flag className="size-3.5 text-warning" aria-label={t("review.flagged")} />
            )}
            <StatusChip status={summary.status} />
          </span>
        </div>
        <p
          lang="ar"
          dir="rtl"
          className="news line-clamp-2 text-start text-lg leading-snug font-semibold"
        >
          {summary.headline}
        </p>
        <p className="flex gap-3 text-xs text-muted">
          <span>{t("review.sourcesCount", { n: fmt.number(summary.source_count) })}</span>
          {summary.duplicate_count > 0 && (
            <span>{t("review.foldedCount", { n: fmt.number(summary.duplicate_count) })}</span>
          )}
        </p>
      </Link>
    </li>
  );
}

function ImagePreview({
  summary,
  onRegenerate,
  busy,
}: {
  summary: SummaryDetail;
  onRegenerate: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const [variant, setVariant] = useState<"landscape" | "square">("landscape");
  const image = summary.images.find((img) => img.variant === variant) ?? summary.images[0];
  return (
    <figure className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          label={t("review.imageVariant")}
          value={variant}
          onChange={setVariant}
          options={[
            { value: "landscape", label: "1200×675" },
            { value: "square", label: "1080×1080" },
          ]}
        />
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw className="size-3.5" />}
          onClick={onRegenerate}
          loading={busy}
        >
          {t("review.regenerateImage")}
        </Button>
      </div>
      {image ? (
        <a
          href={image.url}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-[var(--radius-control)] border border-line bg-course"
        >
          <img
            src={image.url}
            alt={t("review.imageAlt")}
            width={image.width}
            height={image.height}
            className={cx("h-auto w-full", variant === "square" && "mx-auto max-w-sm")}
            loading="lazy"
          />
        </a>
      ) : (
        <div className="grid aspect-[16/9] place-items-center rounded-[var(--radius-control)] border border-dashed border-line-strong text-sm text-muted">
          <span className="flex items-center gap-2">
            <ImageIcon className="size-4" />
            {t("review.imagePending")}
          </span>
        </div>
      )}
    </figure>
  );
}

function SourceMessage({ message, duplicate }: { message: RawMessage; duplicate?: boolean }) {
  const { t, fmt, has } = useI18n();
  const layerKey = `dedup.${message.dedup_layer}`;
  return (
    <li className={cx("flex flex-col gap-1.5 py-3", duplicate && "opacity-80")}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
        <span className="font-medium text-text">
          @{message.channel.username || message.channel.title}
        </span>
        <span className="num">{fmt.dateTime(message.posted_at)}</span>
        {message.language && message.language !== "ar" && (
          <Chip tone="info">
            {t("review.translatedFrom", { lang: message.language.toUpperCase() })}
          </Chip>
        )}
        {duplicate && message.dedup_layer && has(layerKey) && (
          <Chip tone="muted">{t(layerKey as MessageKey)}</Chip>
        )}
        {message.link && (
          <a
            href={message.link}
            target="_blank"
            rel="noreferrer"
            className="ms-auto inline-flex items-center gap-1 text-accent-text hover:underline"
          >
            {t("review.openInTelegram")}
            <ExternalLink className="size-3" />
          </a>
        )}
      </div>
      <p dir="auto" className="text-sm whitespace-pre-line">
        {message.text || <span className="text-muted">{t("review.mediaOnly")}</span>}
      </p>
    </li>
  );
}

function SourcesSheet({
  summary,
  open,
  onOpenChange,
}: {
  summary: SummaryDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, fmt } = useI18n();
  const { data, isLoading } = useSummarySources(summary.id, open);
  const [showDuplicates, setShowDuplicates] = useState(false);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-[color-mix(in_oklab,var(--c-rail)_40%,transparent)]" />
        <RadixDialog.Content className="fixed inset-y-0 end-0 z-50 flex w-[30rem] max-w-full flex-col border-s border-line bg-surface">
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <RadixDialog.Title className="text-lg font-semibold">
                {t("review.sourcesTitle")}
              </RadixDialog.Title>
              <RadixDialog.Description className="mt-1 text-sm text-muted">
                {t("review.sourcesDescription")}
              </RadixDialog.Description>
            </div>
            <RadixDialog.Close
              aria-label={t("common.close")}
              className="rounded p-1 text-muted hover:bg-course hover:text-text"
            >
              <X className="size-4" />
            </RadixDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5">
            {isLoading || !data ? (
              <div className="py-8 text-muted">
                <Spinner label={t("common.loading")} />
              </div>
            ) : (
              <>
                <ul className="divide-y divide-line">
                  {data.messages.map((message) => (
                    <SourceMessage key={message.id} message={message} />
                  ))}
                </ul>
                {data.duplicates.length > 0 && (
                  <div className="border-t border-line py-4">
                    <button
                      type="button"
                      onClick={() => setShowDuplicates((v) => !v)}
                      className="flex items-center gap-2 text-sm font-medium text-accent-text hover:underline"
                      aria-expanded={showDuplicates}
                    >
                      <Layers className="size-4" />
                      {showDuplicates
                        ? t("review.hideDuplicates")
                        : t("review.showDuplicates", { n: fmt.number(data.duplicates.length) })}
                    </button>
                    {showDuplicates && (
                      <ul className="mt-2 divide-y divide-line">
                        {data.duplicates.map((message) => (
                          <SourceMessage key={message.id} message={message} duplicate />
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function RunDetails({ summary }: { summary: SummaryDetail }) {
  const { t, fmt, has } = useI18n();
  const run = summary.run;
  const rows: [string, React.ReactNode][] = [
    [t("review.model"), <span className="break-all">{run.model_used || run.model_requested}</span>],
    [t("review.cost"), <span className="num">{fmt.usd(run.cost_usd, 4)}</span>],
    [
      t("review.tokens"),
      <span className="num">
        {fmt.number(run.prompt_tokens)} / {fmt.number(run.completion_tokens)}
      </span>,
    ],
    [
      t("review.latency"),
      <span className="num">{fmt.number(Math.round(run.latency_ms / 100) / 10)} s</span>,
    ],
    [t("review.attempts"), <span className="num">{fmt.number(run.attempts)}</span>],
    [t("review.messagesIn"), <span className="num">{fmt.number(run.message_count)}</span>],
  ];
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t("review.runDetails")}</h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t("review.publishing")}</h3>
        {summary.jobs.length === 0 ? (
          <p className="text-sm text-muted">{t("review.notPublishedYet")}</p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm">
            {summary.jobs.map((job) => (
              <li key={job.id} className="flex items-center justify-between gap-3">
                <span>{t(`platform.${job.platform}` as MessageKey)}</span>
                <span className="flex items-center gap-2">
                  {job.external_url && (
                    <a
                      href={job.external_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent-text hover:underline"
                    >
                      {t("review.viewPost")}
                    </a>
                  )}
                  <StatusChip status={job.status} />
                </span>
              </li>
            ))}
          </ul>
        )}
        <h3 className="mt-6 mb-2 text-sm font-semibold">{t("review.history")}</h3>
        <ol className="flex flex-col gap-1.5 text-sm">
          {summary.revisions.map((revision) => {
            const key = `revision.${revision.action}`;
            return (
              <li key={revision.id} className="flex justify-between gap-3">
                <span className="flex gap-2">
                  {has(key) ? t(key as MessageKey) : revision.action}
                  {revision.user && <span className="text-muted">{revision.user}</span>}
                </span>
                <span className="num shrink-0 text-muted">{fmt.dateTime(revision.created_at)}</span>
              </li>
            );
          })}
          <li className="flex justify-between gap-3">
            <span>{t("revision.created")}</span>
            <span className="num shrink-0 text-muted">{fmt.dateTime(summary.created_at)}</span>
          </li>
        </ol>
      </section>
    </div>
  );
}

function Proof({ id, onBack }: { id: number; onBack: () => void }) {
  const { t, fmt, dir } = useI18n();
  const toast = useToast();
  const { data: summary, isLoading, error } = useSummary(id);
  const action = useSummaryAction();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ headline: "", body: "", bullets: "" });
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [sourcesOpen, setSourcesOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="grid min-h-60 place-items-center text-muted">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }
  if (error || !summary)
    return (
      <Banner tone="danger" title={t("common.loadFailed")}>
        {error?.message}
      </Banner>
    );

  const run = (
    name: Parameters<typeof action.mutate>[0]["action"],
    body?: unknown,
    done?: MessageKey,
  ) =>
    action.mutate(
      { id: summary.id, action: name, body },
      {
        onSuccess: () => done && toast(t(done)),
        onError: (err) =>
          toast(err instanceof ApiError ? err.message : t("common.actionFailed"), "error"),
      },
    );

  const startEdit = () => {
    setDraft({
      headline: summary.headline,
      body: summary.body,
      bullets: summary.bullet_points.join("\n"),
    });
    setEditing(true);
  };
  const saveEdit = () =>
    action.mutate(
      {
        id: summary.id,
        action: "edit",
        body: {
          headline: draft.headline,
          body: draft.body,
          bullet_points: draft.bullets
            .split("\n")
            .map((b) => b.trim())
            .filter(Boolean),
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast(t("review.saved"));
        },
        onError: (err) =>
          toast(err instanceof ApiError ? err.message : t("common.actionFailed"), "error"),
      },
    );

  const busy = action.isPending ? action.variables?.action : undefined;
  const canEdit = summary.status !== "published";
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  return (
    <article className="flex flex-col gap-6">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 self-start text-sm text-muted hover:text-text xl:hidden"
      >
        <BackIcon className="size-4" />
        {t("review.backToQueue")}
      </button>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted">
        <StatusChip status={summary.status} />
        {summary.kind === "digest" && <Chip tone="info">{t("review.digest")}</Chip>}
        <WindowLabel summary={summary} />
        <button
          type="button"
          onClick={() => setSourcesOpen(true)}
          className="inline-flex items-center gap-1.5 font-medium text-accent-text hover:underline"
        >
          <Layers className="size-4" />
          {t("review.sourcesButton", { n: fmt.number(summary.source_count) })}
          {summary.duplicate_count > 0 && (
            <span className="font-normal text-muted">
              ({t("review.foldedCount", { n: fmt.number(summary.duplicate_count) })})
            </span>
          )}
        </button>
      </div>

      {summary.sensitivity_flag && (
        <Banner tone="warning" title={t("review.flaggedTitle")}>
          <span lang="ar" dir="rtl" className="block text-start">
            {summary.sensitivity_reason}
          </span>
        </Banner>
      )}
      {summary.status === "rejected" && summary.rejection_reason && (
        <Banner tone="danger" title={t("review.rejectedBecause")}>
          {summary.rejection_reason}
        </Banner>
      )}

      {editing ? (
        <div lang="ar" dir="rtl" className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" dir={dir}>
              {t("review.headline")}{" "}
              <span className="num text-muted">({fmt.number(draft.headline.length)}/90)</span>
            </span>
            <textarea
              className="field-input news text-2xl leading-snug font-semibold"
              rows={2}
              value={draft.headline}
              onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" dir={dir}>
              {t("review.body")}
            </span>
            <textarea
              className="field-input news text-xl leading-relaxed"
              rows={6}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" dir={dir}>
              {t("review.bullets")}{" "}
              <span className="font-normal text-muted">{t("review.bulletsHint")}</span>
            </span>
            <textarea
              className="field-input news text-lg leading-relaxed"
              rows={5}
              value={draft.bullets}
              onChange={(e) => setDraft({ ...draft, bullets: e.target.value })}
            />
          </label>
        </div>
      ) : (
        <div lang="ar" dir="rtl" className="news max-w-[60ch] text-start">
          <h2 className="text-[2.15rem] leading-[1.25] font-semibold">{summary.headline}</h2>
          <p className="mt-4 text-[1.4rem] leading-[1.75]">{summary.body}</p>
          {summary.bullet_points.length > 0 && (
            <ul className="mt-4 flex flex-col gap-1.5 text-[1.25rem] leading-[1.6]">
              {summary.bullet_points.map((point, i) => (
                <li key={i} className="flex gap-3">
                  <span aria-hidden className="mt-[0.7em] size-1.5 shrink-0 bg-accent" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center gap-2 border-t border-line bg-bg/95 px-1 py-3 backdrop-blur">
        {editing ? (
          <>
            <Button
              variant="primary"
              icon={<Check className="size-4" />}
              onClick={saveEdit}
              loading={busy === "edit"}
            >
              {t("review.saveChanges")}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
          </>
        ) : (
          <>
            {summary.status === "draft" && (
              <Button
                variant="primary"
                icon={<Check className="size-4" />}
                onClick={() => run("approve", undefined, "review.approvedToast")}
                loading={busy === "approve"}
              >
                {t("review.approve")}
              </Button>
            )}
            {(summary.status === "draft" || summary.status === "approved") && (
              <Button
                variant={summary.status === "approved" ? "primary" : "secondary"}
                icon={<Send className="size-4" />}
                onClick={() => run("publish-now", undefined, "review.publishingToast")}
                loading={busy === "publish-now"}
              >
                {t("review.publishNow")}
              </Button>
            )}
            {canEdit && (
              <Button variant="ghost" icon={<Pencil className="size-4" />} onClick={startEdit}>
                {t("review.edit")}
              </Button>
            )}
            {(summary.status === "rejected" || summary.status === "approved") && (
              <Button
                variant="ghost"
                icon={<RotateCcw className="size-4" />}
                onClick={() => run("reopen", undefined, "review.reopenedToast")}
                loading={busy === "reopen"}
              >
                {t("review.reopen")}
              </Button>
            )}
            {(summary.status === "draft" || summary.status === "approved") && (
              <Button
                variant="danger-outline"
                icon={<X className="size-4" />}
                onClick={() => setRejecting(true)}
                className="ms-auto"
              >
                {t("review.reject")}
              </Button>
            )}
          </>
        )}
      </div>

      <ImagePreview
        summary={summary}
        onRegenerate={() => run("regenerate-image", undefined, "review.imageRegenerated")}
        busy={busy === "regenerate-image"}
      />

      <RunDetails summary={summary} />

      <SourcesSheet summary={summary} open={sourcesOpen} onOpenChange={setSourcesOpen} />

      <Dialog
        open={rejecting}
        onOpenChange={setRejecting}
        title={t("review.rejectTitle")}
        description={t("review.rejectDescription")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={busy === "reject"}
              onClick={() =>
                action.mutate(
                  { id: summary.id, action: "reject", body: { reason } },
                  {
                    onSuccess: () => {
                      setRejecting(false);
                      setReason("");
                      toast(t("review.rejectedToast"));
                    },
                  },
                )
              }
            >
              {t("review.reject")}
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">
            {t("review.reason")}{" "}
            <span className="font-normal text-muted">{t("common.optional")}</span>
          </span>
          <textarea
            className="field-input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </Dialog>
    </article>
  );
}

export function ReviewPage() {
  const { t } = useI18n();
  const params = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("draft");
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  const filters = useMemo(() => ({ ...FILTERS[filter], q: q || undefined }), [filter, q]);
  const { data, isLoading } = useSummaries(filters);
  const { data: draftCounts } = useSummaries({ status: "draft" });

  const selectedId = params.id ? Number(params.id) : (data?.results[0]?.id ?? null);

  const options: { value: Filter; label: string; count?: number }[] = [
    { value: "draft", label: t("review.filterDraft"), count: draftCounts?.count },
    { value: "flagged", label: t("review.filterFlagged") },
    { value: "approved", label: t("status.approved") },
    { value: "published", label: t("status.published") },
    { value: "rejected", label: t("status.rejected") },
    { value: "all", label: t("review.filterAll") },
  ];

  return (
    <div>
      <PageHeader title={t("review.title")} description={t("review.description")} />
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Segmented
          label={t("review.filterLabel")}
          value={filter}
          onChange={setFilter}
          options={options}
        />
        <label className="relative ms-auto w-full sm:w-64">
          <span className="sr-only">{t("review.search")}</span>
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            className="field-input ps-8"
            type="search"
            placeholder={t("review.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      <div className="grid items-start gap-8 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <div
          className={cx(
            "overflow-hidden rounded-[var(--radius-panel)] border border-line bg-course",
            params.id && "hidden xl:block",
          )}
        >
          {isLoading ? (
            <div className="p-6 text-muted">
              <Spinner label={t("common.loading")} />
            </div>
          ) : data && data.results.length > 0 ? (
            <ul className="max-h-[calc(100dvh-14rem)] overflow-y-auto">
              {data.results.map((summary) => (
                <QueueItem key={summary.id} summary={summary} active={summary.id === selectedId} />
              ))}
            </ul>
          ) : (
            <div className="p-4">
              <Empty
                title={filter === "draft" ? t("review.emptyDraftsTitle") : t("review.emptyTitle")}
              >
                {filter === "draft" ? t("review.emptyDraftsBody") : t("review.emptyBody")}
              </Empty>
            </div>
          )}
        </div>
        <div className={cx(!params.id && "hidden xl:block")}>
          {selectedId ? (
            <Proof key={selectedId} id={selectedId} onBack={() => navigate("/review")} />
          ) : (
            !isLoading && <p className="text-sm text-muted">{t("review.pickOne")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
